/**
 * PastDueBanner — global banner shown above the entire authenticated app
 * when the active org has a non-active billing status. Backend returns
 * 402 on every write for past_due orgs (see `enforcePlanStatus` in
 * apps/api/src/middleware/tenant.ts), so a global surface is the only
 * way users discover what's wrong before hitting random save failures.
 *
 * Polls /api/billing/plan every 5min. Uses plain useEffect rather than
 * @tanstack/react-query to avoid coupling the plugin route surface to
 * the QueryClient instance Flowlib mounts.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, type LucideIcon } from 'lucide-react';
import { useApiClient } from '@flowlib/ui';
import { getCurrentPlan, type CurrentPlan } from './billing-api';

const REFRESH_MS = 5 * 60 * 1000;
const AlertIcon = AlertTriangle as unknown as LucideIcon;

export function PastDueBanner() {
  const api = useApiClient();
  const apiBase = api.getBaseURL();
  const [plan, setPlan] = useState<CurrentPlan | null>(null);

  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchPlan = async () => {
      try {
        const p = await getCurrentPlan(apiBase);
        if (!cancelled) {
          setPlan(p);
        }
      } catch {
        // 401/403/network — silently ignore. Banner just won't render.
      }
    };

    void fetchPlan();
    intervalId = setInterval(fetchPlan, REFRESH_MS);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [apiBase]);

  if (!plan || plan.status === 'active') {
    return null;
  }

  const { message, severity } = bannerCopy(plan);
  const colour =
    severity === 'critical'
      ? 'bg-fl-destructive/15 border-fl-destructive/40 text-fl-destructive'
      : 'bg-fl-muted border-fl-border text-fl-foreground';

  return (
    <div
      role="alert"
      className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-sm ${colour}`}
    >
      <div className="flex items-center gap-2">
        <AlertIcon className="h-4 w-4" aria-hidden />
        <span>{message}</span>
      </div>
      <a
        href="/settings/billing"
        className="rounded border border-current px-2 py-0.5 text-xs hover:bg-fl-background/40"
      >
        Manage billing
      </a>
    </div>
  );
}

function bannerCopy(plan: CurrentPlan): { message: string; severity: 'critical' | 'warning' } {
  if (plan.status === 'suspended') {
    return {
      message: 'Your workspace is suspended. Contact support to restore access.',
      severity: 'critical',
    };
  }
  if (plan.status === 'past_due') {
    return {
      message:
        'Payment past due. Workspace writes are blocked until you update your payment method.',
      severity: 'critical',
    };
  }
  if (plan.cancelAtPeriodEnd && plan.cancelAt) {
    const date = new Date(plan.cancelAt * 1000).toLocaleDateString();
    return {
      message: `Your ${plan.planName} plan ends on ${date}. Renew to keep your subscription.`,
      severity: 'warning',
    };
  }
  return {
    message: `Subscription status: ${plan.status}.`,
    severity: 'warning',
  };
}
